import { useTranslation } from "react-i18next"

import type { CapabilityMode } from "@/api/admin/delegation"

import { Badge } from "@/components/ui/badge"

import { useDelegationMatrixQuery } from "../../hooks/queries/delegation"

type FairUseDelegationPanelProps = {
  isPlatformAdmin: boolean
}

const MODE_FALLBACK_LABELS: Record<CapabilityMode, string> = {
  hidden: "Not see fair use at all",
  available: "See fair use, without changing it",
  manage: "Manage limits, plans per person, requests and local AI leads",
}

/**
 * What the tenant's own admins may change — explained, not duplicated.
 *
 * One capability now governs the whole Fair use area, the local AI lead roster
 * included, so this is the only place the line between tenant and platform is
 * drawn: limits, plans per person, requests and leads on one side, the
 * commercial envelope on the other.
 *
 * The control itself is the `DelegationModeControl` beside this panel's heading,
 * which is the same selector every other delegable section carries and writes
 * through the delegation matrix rather than the fair-use config. A second
 * selector here would be a second source of truth for one value: whichever
 * one was not touched would go stale the moment the other was used, and the
 * mutation's cache invalidation would make them disagree mid-interaction.
 *
 * So this panel reads. It names the current mode, says what it means in terms
 * of this feature rather than the generic hidden/available/manage ladder, and
 * points at the control. For a tenant admin the matrix query is not even
 * issued (it is platform-admin-only), so they get the explanation without a
 * mode — which is the honest answer to "what may I change here": ask your
 * platform provider.
 */
export function FairUseDelegationPanel({ isPlatformAdmin }: FairUseDelegationPanelProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useDelegationMatrixQuery(isPlatformAdmin)
  const mode = data?.capabilities["fair_use"]

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground max-w-prose text-sm">
        {t(
          "adminDashboard.fairUse.settings.delegation.caption",
          "What the tenant's own admins may change in the Fair use area."
        )}
      </p>

      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            {t("adminDashboard.fairUse.settings.delegation.currentModeLabel", "Tenant admins can")}
          </span>
          {isPlatformAdmin && !isLoading ? (
            <Badge data-testid="fair-use-delegation-mode" variant="secondary">
              {t(
                `adminDashboard.fairUse.settings.delegation.mode.${mode ?? "hidden"}`,
                MODE_FALLBACK_LABELS[mode ?? "hidden"]
              )}
            </Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground max-w-prose text-sm">
          {t(
            "adminDashboard.fairUse.settings.delegation.body",
            "On Manage, tenant admins turn fair use on or off, move the four steps and pick the fallback model, they answer requests and set a person's plan, and they keep the local AI lead roster and its approval allowance. Plans & pricing stay with the platform in every mode."
          )}
        </p>
        <p className="text-muted-foreground text-xs">
          {isPlatformAdmin
            ? t(
                "adminDashboard.fairUse.settings.delegation.controlHint",
                "Change this with the selector above."
              )
            : t(
                "adminDashboard.fairUse.settings.delegation.unavailable",
                "Only your platform provider can change this."
              )}
        </p>
      </div>
    </div>
  )
}
